import PauseIcon from '@mui/icons-material/Pause';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import ReplayIcon from '@mui/icons-material/Replay';
import RepeatIcon from '@mui/icons-material/Repeat';
import ClearIcon from '@mui/icons-material/Clear';
import {
  Box,
  Button,
  IconButton,
  MenuItem,
  Select,
  Slider,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  Tooltip,
  useTheme,
} from '@mui/material';

import { getHeaderChrome, headerReplayBarSimpleSx, headerReplayPlayButtonSx } from '@kangaroo/ui';

import { formatTraceClock, type ReplayScope } from '../../lib/telemetrySession';
import { useLivePlayhead } from '../../lib/useLivePlayhead';
import { useSmoothedNumber } from '../../lib/useSmoothedNumber';
import type { TelemetryReplayControls } from '../../lib/useTelemetryReplay';

const PLAYBACK_RATES = [0.5, 1, 2, 4];

function ReplayLoopControls({
  replay,
  isDark,
  size = 28,
}: {
  replay: TelemetryReplayControls;
  isDark: boolean;
  size?: number;
}) {
  const rangeReady = replay.loopStartSec !== null && replay.loopEndSec !== null;
  const buttonSx = {
    width: size,
    minWidth: size,
    height: size,
    p: 0,
    borderColor: isDark ? 'rgba(148,163,184,0.3)' : '#D1D5DB',
    color: isDark ? '#CBD5E1' : '#374151',
    fontSize: '0.64rem',
    fontWeight: 900,
  } as const;

  return (
    <Stack direction="row" spacing={0.3} sx={{ alignItems: 'center', flexShrink: 0 }}>
      <Tooltip title={`Set loop start${replay.loopStartSec === null ? '' : ` · ${formatTraceClock(replay.loopStartSec)}`}`}>
        <Button
          variant="outlined"
          onClick={() => replay.setLoopStart()}
          sx={{ ...buttonSx, ...(replay.loopStartSec !== null ? { borderColor: '#2563EB', color: '#2563EB', bgcolor: 'rgba(37,99,235,0.1)' } : {}) }}
        >
          A
        </Button>
      </Tooltip>
      <Tooltip title={`Set loop end${replay.loopEndSec === null ? '' : ` · ${formatTraceClock(replay.loopEndSec)}`}`}>
        <Button
          variant="outlined"
          onClick={() => replay.setLoopEnd()}
          sx={{ ...buttonSx, ...(replay.loopEndSec !== null ? { borderColor: '#EA580C', color: '#EA580C', bgcolor: 'rgba(234,88,12,0.1)' } : {}) }}
        >
          B
        </Button>
      </Tooltip>
      <Tooltip title={replay.loopEnabled ? 'Pause section repeat' : 'Repeat selected section'}>
        <span>
          <IconButton
            disabled={!rangeReady}
            onClick={replay.toggleLoop}
            aria-label={replay.loopEnabled ? 'Disable section repeat' : 'Enable section repeat'}
            sx={{
              width: size,
              height: size,
              border: '1px solid',
              borderColor: replay.loopEnabled ? '#059669' : isDark ? 'rgba(148,163,184,0.3)' : '#D1D5DB',
              bgcolor: replay.loopEnabled ? 'rgba(5,150,105,0.12)' : 'transparent',
              color: replay.loopEnabled ? '#059669' : isDark ? '#CBD5E1' : '#4B5563',
            }}
          >
            <RepeatIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </span>
      </Tooltip>
      {replay.loopStartSec !== null || replay.loopEndSec !== null ? (
        <Tooltip title="Clear loop">
          <IconButton onClick={replay.clearLoop} aria-label="Clear replay loop" sx={{ width: size, height: size, color: isDark ? '#94A3B8' : '#6B7280' }}>
            <ClearIcon sx={{ fontSize: 15 }} />
          </IconButton>
        </Tooltip>
      ) : null}
    </Stack>
  );
}

type TelemetryReplayBarProps = {
  replay: TelemetryReplayControls;
  scope: ReplayScope;
  onScopeChange: (scope: ReplayScope) => void;
  lapNumber: number;
  totalLaps: number;
  segmentAvailable?: boolean;
  segmentLabel?: string | null;
  sessionScopeLabel?: string;
  sessionLapCountLabel?: string;
  lapScopeLabel?: string | null;
  compact?: boolean;
  mini?: boolean;
  simple?: boolean;
  bare?: boolean;
  sidebar?: boolean;
  showScopeToggle?: boolean;
  controlsDisabled?: boolean;
  forceDark?: boolean;
};

export function TelemetryReplayBar({
  replay,
  scope,
  onScopeChange,
  lapNumber,
  totalLaps,
  segmentAvailable = false,
  segmentLabel = null,
  sessionScopeLabel = 'Full session',
  sessionLapCountLabel,
  lapScopeLabel = null,
  compact = false,
  mini = false,
  simple = false,
  bare = false,
  sidebar = false,
  showScopeToggle = true,
  controlsDisabled = false,
  forceDark = false,
}: TelemetryReplayBarProps) {
  const theme = useTheme();
  const livePlayhead = useLivePlayhead(replay);
  const displayElapsedSec = useSmoothedNumber(livePlayhead.elapsedSec, 80, replay.isPlaying)
    ?? (replay.isPlaying ? livePlayhead.elapsedSec : replay.elapsedSec);
  const isDark = forceDark || theme.palette.mode === 'dark';
  const headerChrome = getHeaderChrome(isDark);
  const disabled = controlsDisabled || replay.durationSec <= 0;
  const sliderElapsedSec = Math.min(
    replay.durationSec,
    Math.max(0, replay.isPlaying ? livePlayhead.elapsedSec : replay.elapsedSec),
  );
  const controlSize = mini ? 28 : sidebar ? 38 : 34;
  const controlIconSize = mini ? 16 : sidebar ? 20 : 18;
  const toggleFontSize = mini ? '0.6rem' : sidebar ? '0.74rem' : compact ? '0.66rem' : '0.66rem';
  const metaFontSize = mini ? '0.58rem' : sidebar ? '0.74rem' : '0.64rem';
  const metaLabel = scope === 'session'
    ? `${lapScopeLabel ?? `Lap ${lapNumber}`} | ${sessionLapCountLabel ?? `${totalLaps} laps`}`
    : scope === 'segment'
      ? `${segmentLabel ?? `Lap ${lapNumber} segment`} | ${formatTraceClock(replay.durationSec)}`
      : lapScopeLabel ?? `Lap ${lapNumber} | ${formatTraceClock(replay.durationSec)}`;

  if (simple) {
    return (
      <Box sx={headerReplayBarSimpleSx(isDark)}>
        <IconButton
          aria-label={replay.isPlaying ? 'Pause replay' : 'Play replay'}
          disabled={disabled}
          onClick={replay.togglePlay}
          sx={headerReplayPlayButtonSx(isDark, replay.isPlaying)}
        >
          {replay.isPlaying ? <PauseIcon sx={{ fontSize: 16 }} /> : <PlayArrowIcon sx={{ fontSize: 16 }} />}
        </IconButton>

        <Select
          size="small"
          value={replay.playbackRate}
          disabled={disabled}
          onChange={(event) => replay.setPlaybackRate(Number(event.target.value))}
          inputProps={{ 'aria-label': 'Replay speed' }}
          sx={{
            width: 54,
            height: 28,
            flexShrink: 0,
            fontSize: '0.64rem',
            fontWeight: 800,
            color: headerChrome.replayTimeText,
            bgcolor: headerChrome.replayMetaBg,
            '.MuiOutlinedInput-notchedOutline': {
              borderColor: headerChrome.replayMetaBorder,
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: headerChrome.replayPlayBorder,
            },
            '& .MuiSelect-select': {
              py: 0.35,
              pl: 0.75,
              pr: '22px !important',
            },
            '& .MuiSelect-icon': {
              right: 3,
              color: headerChrome.replayMetaText,
              fontSize: 16,
            },
          }}
        >
          {PLAYBACK_RATES.map((rate) => (
            <MenuItem key={rate} value={rate}>
              {rate}x
            </MenuItem>
          ))}
        </Select>

        <ReplayLoopControls replay={replay} isDark={isDark} size={28} />

        <Typography
          sx={{
            width: 38,
            flexShrink: 0,
            fontSize: '0.64rem',
            color: headerChrome.replayTimeText,
            fontWeight: 850,
            fontVariantNumeric: 'tabular-nums',
            textAlign: 'right',
          }}
        >
          {formatTraceClock(displayElapsedSec)}
        </Typography>

        <Slider
          size="small"
          disabled={disabled}
          value={sliderElapsedSec}
          min={0}
          max={Math.max(replay.durationSec, 0.001)}
          step={0.01}
          onChange={(_, value) => replay.seekElapsed(Array.isArray(value) ? value[0]! : value)}
          sx={{
            flex: 1,
            minWidth: 72,
            color: '#B78CFF',
            height: 4,
            py: 0.25,
            '& .MuiSlider-rail': {
              opacity: 1,
              bgcolor: isDark ? 'rgba(148,163,184,0.26)' : 'rgba(107,114,128,0.22)',
            },
            '& .MuiSlider-track': {
              border: 0,
              background: 'linear-gradient(90deg, #B78CFF 0%, #00D5FF 100%)',
            },
            '& .MuiSlider-thumb': {
              width: 10,
              height: 10,
              bgcolor: '#FFFFFF',
              border: '2px solid #B78CFF',
              boxShadow: '0 0 0 4px rgba(183,140,255,0.18)',
              '&:hover, &.Mui-focusVisible': {
                boxShadow: '0 0 0 6px rgba(183,140,255,0.24)',
              },
            },
          }}
        />

        <Typography
          sx={{
            width: 38,
            flexShrink: 0,
            fontSize: '0.64rem',
            color: headerChrome.replayMetaText,
            fontWeight: 800,
            fontVariantNumeric: 'tabular-nums',
          }}
        >
          {formatTraceClock(replay.durationSec)}
        </Typography>

        <Box
          sx={{
            display: { xs: 'none', xl: 'block' },
            maxWidth: 108,
            px: 0.75,
            py: 0.35,
            borderRadius: '6px',
            bgcolor: headerChrome.replayMetaBg,
            border: `1px solid ${headerChrome.replayMetaBorder}`,
            color: headerChrome.replayMetaLabel,
            fontSize: '0.62rem',
            fontWeight: 850,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {metaLabel}
        </Box>
      </Box>
    );
  }

  return (
    <Box
      sx={
        bare
          ? undefined
          : {
              borderRadius: '6px',
              border: simple ? '1px solid rgba(148,163,184,0.32)' : '1px solid rgba(36,62,82,0.55)',
              bgcolor: simple ? 'rgba(1,13,24,0.86)' : 'rgba(1,13,24,0.45)',
              backdropFilter: simple ? 'none' : 'blur(8px)',
              boxShadow: simple ? '0 10px 26px rgba(0,0,0,0.34)' : undefined,
              px: simple ? 0.55 : mini ? 0.75 : 1,
              py: simple ? 0.35 : mini ? 0.45 : 0.65,
            }
      }
    >
      <Stack direction={simple ? 'row' : compact ? 'column' : { xs: 'column', md: 'row' }} spacing={simple ? 0.85 : mini ? 0.55 : 1.25} sx={{ alignItems: simple ? 'center' : compact ? 'stretch' : { md: 'center' } }}>
        <Stack
          direction="row"
          spacing={mini ? 0.45 : 0.75}
          useFlexGap={compact}
          sx={{
            alignItems: 'center',
            flexShrink: 0,
            flexWrap: compact ? 'wrap' : 'nowrap',
            gap: compact ? (mini ? 0.45 : 0.75) : undefined,
          }}
        >
          <IconButton
            aria-label={replay.isPlaying ? 'Pause replay' : 'Play replay'}
            disabled={disabled}
            onClick={replay.togglePlay}
            sx={{
              width: controlSize,
              height: controlSize,
              border: simple ? '1px solid rgba(255,255,255,0.32)' : '1px solid rgba(36,62,82,0.78)',
              color: '#FFFFFF',
              bgcolor: simple ? 'rgba(183,140,255,0.32)' : 'rgba(168,85,247,0.18)',
              '&:hover': {
                bgcolor: simple ? 'rgba(183,140,255,0.46)' : 'rgba(168,85,247,0.26)',
              },
            }}
          >
            {replay.isPlaying ? <PauseIcon sx={{ fontSize: controlIconSize }} /> : <PlayArrowIcon sx={{ fontSize: controlIconSize }} />}
          </IconButton>
          {!simple ? (
            <IconButton
              aria-label="Restart replay"
              disabled={disabled}
              onClick={replay.restart}
              sx={{ width: controlSize, height: controlSize, border: '1px solid rgba(36,62,82,0.78)', color: '#A6B0BE' }}
            >
              <ReplayIcon sx={{ fontSize: controlIconSize - 1 }} />
            </IconButton>
          ) : null}
          {!simple ? (
            <Select
              size="small"
              value={replay.playbackRate}
              disabled={disabled}
              onChange={(event) => replay.setPlaybackRate(Number(event.target.value))}
              sx={{
                minWidth: mini ? 62 : sidebar ? 78 : 72,
                height: controlSize,
                fontSize: mini ? '0.66rem' : sidebar ? '0.76rem' : '0.72rem',
                color: '#FFFFFF',
                '.MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(36,62,82,0.78)' },
              }}
            >
              {PLAYBACK_RATES.map((rate) => (
                <MenuItem key={rate} value={rate}>
                  {rate}x
                </MenuItem>
              ))}
            </Select>
          ) : null}
          {!simple ? <ReplayLoopControls replay={replay} isDark={isDark} size={controlSize} /> : null}
          <ToggleButtonGroup
            exclusive
            size="small"
            value={scope}
            onChange={(_, value: ReplayScope | null) => {
              if (value) {
                onScopeChange(value);
              }
            }}
            sx={{
              display: showScopeToggle ? undefined : 'none',
              flexWrap: compact ? 'wrap' : 'nowrap',
              '& .MuiToggleButton-root': {
                px: mini ? 0.7 : compact || sidebar ? 1 : 1.1,
                py: mini ? 0.3 : sidebar ? 0.55 : 0.45,
                fontSize: toggleFontSize,
                fontWeight: 800,
                color: '#A6B0BE',
                borderColor: 'rgba(36,62,82,0.78)',
                textTransform: 'none',
              },
              '& .Mui-selected': {
                color: '#FFFFFF !important',
                bgcolor: 'rgba(168,85,247,0.22) !important',
              },
            }}
          >
            <ToggleButton value="lap">This lap</ToggleButton>
            {segmentAvailable ? <ToggleButton value="segment">Segment</ToggleButton> : null}
            <ToggleButton value="session">{sessionScopeLabel}</ToggleButton>
          </ToggleButtonGroup>
        </Stack>

        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Slider
            size="small"
            disabled={disabled}
            value={sliderElapsedSec}
            min={0}
            max={Math.max(replay.durationSec, 0.001)}
            step={0.01}
            onChange={(_, value) => replay.seekElapsed(Array.isArray(value) ? value[0]! : value)}
            sx={{
              color: simple ? '#B78CFF' : '#A855F7',
              height: simple ? 4 : undefined,
              py: simple ? 0.3 : undefined,
              '& .MuiSlider-rail': {
                opacity: simple ? 1 : 0.25,
                bgcolor: simple ? 'rgba(148,163,184,0.28)' : undefined,
              },
              '& .MuiSlider-track': {
                border: 0,
              },
              '& .MuiSlider-thumb': {
                width: simple ? 9 : mini ? 10 : sidebar ? 14 : 12,
                height: simple ? 9 : mini ? 10 : sidebar ? 14 : 12,
                boxShadow: simple ? '0 0 0 3px rgba(183,140,255,0.18)' : undefined,
              },
            }}
          />
          <Stack
            direction="row"
            sx={{
              justifyContent: 'space-between',
              mt: simple ? 0 : -0.25,
              gap: 1,
            }}
          >
            <Typography sx={{ fontSize: metaFontSize, color: '#A6B0BE' }}>
              {formatTraceClock(displayElapsedSec)}
            </Typography>
            <Typography sx={{ fontSize: metaFontSize, color: '#A6B0BE', textAlign: 'right' }}>
              {metaLabel}
            </Typography>
          </Stack>
        </Box>
      </Stack>
    </Box>
  );
}
